import { format } from 'date-fns';

import { Checkbox, Icon, Tag, Text } from '@nvision/ui';

import { Card } from '@/components/card';

import type { Accessorial } from '../../../models';

import styles from './accessorial-card.module.css';

interface AccessorialCardProps {
  accessorial: Accessorial;
}

const DATE_FORMAT = 'dd-MMM-yyyy';
const EMPTY_VALUE = '—';

const formatDateValue = (value: string | null | undefined) => (value ? format(value, DATE_FORMAT) : EMPTY_VALUE);

export const AccessorialCard: React.FC<AccessorialCardProps> = ({ accessorial }) => {
  return (
    <Card>
      <Card.Header>
        <div className={styles.headerContainer}>
          <div className={styles.headerContent}>
            <div className={styles.keyContainer}>
              <div className={styles.keyContent}>
                <Checkbox className={styles.checkbox} size="sm" />
                <Text size="sm" slot="description" weight="semibold">
                  Accessorial Key
                </Text>
                <Tag success>
                  <Text size="sm">{accessorial.accessorialKey}</Text>
                </Tag>
              </div>
            </div>
            <div className={styles.summary}>
              <Card.Field horizontal>
                <Text size="sm" elementType="div">
                  Accessorial Name:
                </Text>
                <Text size="sm" elementType="div" weight="semibold">
                  {accessorial.accessorialName || EMPTY_VALUE}
                </Text>
              </Card.Field>
              <Card.Field horizontal>
                <Text size="sm" elementType="div">
                  Effective From:
                </Text>
                <Text size="sm" elementType="div" weight="semibold">
                  {formatDateValue(accessorial.effectiveFromDate)}
                </Text>
              </Card.Field>
              <Card.Field horizontal>
                <Text size="sm" elementType="div">
                  Effective To:
                </Text>
                <Text size="sm" elementType="div" weight="semibold">
                  {formatDateValue(accessorial.effectiveToDate)}
                </Text>
              </Card.Field>
            </div>
          </div>
          <Icon name="ellipsis-vertical" className={styles.menuIcon} />
        </div>
      </Card.Header>
      <Card.Main>
        <div className={styles.cardContent}>
          <div className={styles.cardCol}>
            <Card.Field>
              <Text size="xs" elementType="div">
                Customer
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.customer.label}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Carrier Name
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.carrier.label}
              </Text>
            </Card.Field>
          </div>
          <div className={styles.cardCol}>
            <Card.Field>
              <Text size="xs" elementType="div">
                Service Description
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.service.label}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Branch Name
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.branch.label}
              </Text>
            </Card.Field>
          </div>
          <div className={styles.cardCol}>
            <Card.Field>
              <Text size="xs" elementType="div">
                Pricing Type
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.pricingType?.label || EMPTY_VALUE}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Accessorial Basis
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.accessorialBasis?.label || EMPTY_VALUE}
              </Text>
            </Card.Field>
          </div>
          <div className={styles.cardCol}>
            <Card.Field>
              <Text size="xs" elementType="div">
                Accessorial Type
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.accessorialType}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Equipment Type
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.equipmentType.label}
              </Text>
            </Card.Field>
          </div>
          <div className={styles.cardCol}>
            <Card.Field>
              <Text size="xs" elementType="div">
                Currency
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.currency?.label || EMPTY_VALUE}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Direct/Indirect
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.directInterline?.label || EMPTY_VALUE}
              </Text>
            </Card.Field>
          </div>
          <div className={styles.cardCol}>
            <Card.Field>
              <Text size="xs" elementType="div">
                Lane Based
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.laneBased || EMPTY_VALUE}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Ship Type
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {accessorial.shipType?.label || EMPTY_VALUE}
              </Text>
            </Card.Field>
          </div>
        </div>
      </Card.Main>
      <Card.Footer className={styles.footer}>
        <div className={styles.cardCol}>
          <Card.Field>
            <Text size="xs" elementType="div">
              Created By
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {accessorial.createdByName}
            </Text>
          </Card.Field>
        </div>
        <div className={styles.cardCol}>
          <Card.Field>
            <Text size="xs" elementType="div">
              Created On
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {formatDateValue(accessorial.createdOn)}
            </Text>
          </Card.Field>
        </div>
        <div className={styles.cardCol}>
          <Card.Field>
            <Text size="xs" elementType="div">
              Modified By
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {accessorial.modifiedByName}
            </Text>
          </Card.Field>
        </div>
        <div className={styles.cardCol}>
          <Card.Field>
            <Text size="xs" elementType="div">
              Modified On
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {formatDateValue(accessorial.modifiedOn)}
            </Text>
          </Card.Field>
        </div>
      </Card.Footer>
    </Card>
  );
};
